import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { t } from 'app/core/i18n/i18n';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import {
  BulkGpsConfigRequest,
  BULK_GPS_CONFIG_MAX_PARTNERS,
} from 'app/modules/components/business-partners/business-partner/interfaces/bulk-gps-config.interface';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import {
  AssignedPartnersSubRouteFilters,
  AssignedPartnersSubRoutePagination,
} from '../../sub-route-partners.types';
import { BusinessPartnerBySubRoute } from '../../../interfaces/busines-partner-by-sub-route.interface';
import {
  BulkGpsUpdatePayload,
  BulkGpsUpdateResult,
  BulkGpsUpdateResultItem,
} from './bulk-gps-update-dialog.types';

@Injectable()
export class BulkGpsUpdateDialogService {
  private readonly businessPartnerService = inject(BusinessPartnerService);
  private readonly handlerMessage = inject(HandlerMessageService);

  readonly maxPartnersPerRequest = BULK_GPS_CONFIG_MAX_PARTNERS;

  findAssignedPartners(
    subRouteId: string,
    pagination: AssignedPartnersSubRoutePagination,
    companyId?: string | null
  ) {
    return this.businessPartnerService.findAssignedToSubRouteBusinessPartnerWithCount(
      {} as AssignedPartnersSubRouteFilters,
      pagination,
      subRouteId,
      companyId ?? null
    );
  }

  submitBulkUpdate(
    payload: BulkGpsUpdatePayload,
    partners: BusinessPartnerBySubRoute[]
  ): Observable<BulkGpsUpdateResult> {
    const body = this.buildRequestBody(payload);
    const partnerMap = new Map(partners.map((p) => [p._id, p]));

    return this.businessPartnerService
      .bulkUpdateGpsConfigBySubRoute(payload.subRouteId, body)
      .pipe(
        map((response) => {
          const data = response.data;
          const updatedIds = new Set(data.businessPartnerIds ?? []);

          const items: BulkGpsUpdateResultItem[] = payload.businessPartnerIds.map((id) => {
            const partner = partnerMap.get(id);
            return {
              businessPartnerId: id,
              code: partner?.externalKeys?.code ?? '-',
              name: partner?.name ?? '-',
              success: updatedIds.has(id),
            };
          });

          const result: BulkGpsUpdateResult = {
            total: payload.businessPartnerIds.length,
            successCount: data.updatedCount ?? items.filter((i) => i.success).length,
            failedCount: items.filter((i) => !i.success).length,
            items,
          };

          if (result.failedCount === 0) {
            this.handlerMessage.success({
              message: t('routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.SUCCESS_UPDATE', {
                count: result.successCount,
              }),
            });
          }

          return result;
        })
      );
  }

  private buildRequestBody(payload: BulkGpsUpdatePayload): BulkGpsConfigRequest {
    const gpsConfig: BulkGpsConfigRequest['gpsConfig'] = {
      useGps: payload.useGps,
    };

    if (payload.useGps) {
      gpsConfig.useGpsMode = payload.useGpsMode;
      gpsConfig.distanceBtwSellerAndCustomer = payload.distanceBtwSellerAndCustomer ?? 0;
    }

    return {
      businessPartnerIds: payload.businessPartnerIds,
      gpsConfig,
    };
  }
}
